import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-final-form';
import { useQuery } from '@apollo/client';
import useSearchModal from '../../../../hooks/useSearchModal';
import {
  hidePopupHeader,
  showPopupHeader,
  setChosenLocationNames,
  setLocationIds,
  setCurrentState
} from '../../../../actions';
import { Input, InputTypeMap } from '../../../ui/Input';
import { Modal } from '../../../ui/Modal';
import { NewSearchPopupLocationsProps } from './types';
import { selectSearchFormLocationResetButtonIsDisabled } from '../../../../store/selectors';
import { LocationsContainer, modalFooterStyles } from './styled';
import { NewSearchLocation } from '../NewSearchLocation';
import { NewSearchLocationBar } from '../NewSearchLocationBar';
import Loader from '../../../Loader';
import { Location } from '../types';
import { getChosenLocationNames } from '../../../../utils/helpers/getChosenLocationNames';
import { getLocationGroups } from '../../../../api/queries';
import { LocationGroupsQuery } from '../../../../graphql/graphql';
import { BackButtonIconEnum } from '../../../ui/PageHeader/types';
import useScreenSize from '../../../../hooks/useScreenSize';

export const NewSearchPopupLocations = ({
  undo,
  isOpen,
  popupTitle,
  closeHandler,
  inputPlaceholder,
  cityId,
  openCitiesHandler,
  setLocationsIsLoading,
  hasLocations,
  modalParams,
  renderSubmitText
}: NewSearchPopupLocationsProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { submit, getState, change } = useForm();
  const { isSmall } = useScreenSize();
  const inputEl = useRef<HTMLInputElement | null>(null);

  const locationsResetButtonIsDisabled = useSelector(
    selectSearchFormLocationResetButtonIsDisabled
  );
  const currentLocationIds = getState().values.location_ids || [];

  const [chosenLocationIds, setChosenLocationIds] =
    useState<string[]>(currentLocationIds);
  const [locations, setLocations] = useState<Location[]>([]);
  const [currentTabId, setCurrentTabId] = useState(0);
  const [townshipsCount, setTownshipsCount] = useState(0);

  const initialScreen = modalParams?.initialScreen;

  const locationGroupsQueryVariables = {
    ids: [1, 2, 3, 4],
    cityId
  };
  const { data, loading } = useQuery<LocationGroupsQuery>(getLocationGroups, {
    variables: locationGroupsQueryVariables
  });

  const {
    isFocused,
    searchString,
    onBlurHandler,
    onFocusHandler,
    onResetHandler,
    onChangeHandler,
    setIsSubmittedForm,
    defaultCancelHandler
  } = useSearchModal(inputEl);

  const handleReset = () => {
    dispatch(setLocationIds([]));
    dispatch(setChosenLocationNames([]));
    defaultCancelHandler();
  };

  const cancelHandler = () => {
    dispatch(showPopupHeader());
    defaultCancelHandler();
  };

  const handleCloseWithUndo = useCallback(() => {
    undo();
    closeHandler();
  }, [undo, closeHandler]);

  const closePopup = () => {
    dispatch(showPopupHeader());
    closeHandler();
    cancelHandler();
  };

  const onConfirm = () => {
    change('location_ids', chosenLocationIds);
    dispatch(setLocationIds(chosenLocationIds));
    dispatch(
      setCurrentState({
        ...getState().values,
        location_ids: chosenLocationIds
      })
    );
    dispatch(
      setChosenLocationNames(
        getChosenLocationNames(locations, chosenLocationIds)
      )
    );
    if (initialScreen === 'locations' || initialScreen === 'city') {
      return submit()?.then(closePopup);
    }
    closePopup();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === 13 && !e.shiftKey) {
      e.preventDefault();
      setIsSubmittedForm(true);
    }
  };

  const onFocusInputHandler = () => {
    dispatch(hidePopupHeader());
    onFocusHandler();
  };

  const { locationGroups = [] } = data || {};
  const nonEmptyLocationGroups = locationGroups.filter(
    group => !!group.locations.length
  );
  const confirmText = renderSubmitText
    ? renderSubmitText({ locationIds: chosenLocationIds })
    : t('search.city_search.ready');

  const submitButtonText =
    initialScreen === 'filters'
      ? t('search.buttons.add_to_search')
      : confirmText;

  return (
    <Modal
      isOpen={isOpen}
      modalId="locations"
      onClose={handleCloseWithUndo}
      cypress="new-search-popup--locations"
      title={popupTitle}
      resetIsDisabled={locationsResetButtonIsDisabled && !searchString}
      onReset={handleReset}
      resetText={t('common.reset')}
      confirmText={submitButtonText}
      onConfirm={onConfirm}
      showHeaderBorder={false}
      footerStyles={modalFooterStyles}
      headerIcon={BackButtonIconEnum.Close}
      closeButtonAlign={isSmall ? 'left' : 'right'}
    >
      <LocationsContainer $loading={loading}>
        <Loader loading={loading}>
          <NewSearchLocationBar
            openCitiesHandler={openCitiesHandler}
            cityId={cityId}
            setLocationsIsLoading={setLocationsIsLoading}
            searchString={searchString}
            chosenLocationIds={chosenLocationIds}
            locationGroups={nonEmptyLocationGroups}
            townshipsCount={townshipsCount}
            currentTabId={currentTabId}
            setCurrentTabId={setCurrentTabId}
          >
            {inputPlaceholder && (
              <Input
                refEl={inputEl}
                value={searchString}
                onKeyDown={handleKeyDown}
                type={InputTypeMap.search}
                onBlurHandler={onBlurHandler}
                placeholder={inputPlaceholder}
                stat="new-search-find-locations"
                onResetHandler={onResetHandler}
                onChangeHandler={onChangeHandler}
                onFocusHandler={onFocusInputHandler}
                isShowReset={!!searchString}
                isInsideModalWithAnimation
              />
            )}
          </NewSearchLocationBar>
        </Loader>
        <NewSearchLocation
          noLocations={!hasLocations}
          locationGroups={nonEmptyLocationGroups}
          isFocused={isFocused}
          searchString={searchString}
          cancelHandler={cancelHandler}
          setChosenLocationIds={setChosenLocationIds}
          chosenLocationIds={chosenLocationIds}
          openCitiesHandler={openCitiesHandler}
          currentTabId={currentTabId}
          setTownshipsCount={setTownshipsCount}
          setLocations={setLocations}
        />
      </LocationsContainer>
    </Modal>
  );
};
